import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material'
import {
  Add as AddIcon,
  LibraryBooks as LibraryBooksIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useNotification } from '../contexts/NotificationContext'
import { catalogApi } from '../services/catalogApi'
import { ContentItem } from '../types/catalog'
import SMBSelector from '../components/SMBSelector'
import { useSMBContext } from '../contexts/SMBContext'
import ContentFilters from '../components/catalog/ContentFilters'
import ContentCard from '../components/catalog/ContentCard'
import AddContentDialog from '../components/catalog/AddContentDialog'
import ConfirmDialog from '../components/dialogs/ConfirmDialog'
import PageBanner from '../components/PageBanner'
import EmptyState from '../components/EmptyState'
import { CatalogGridSkeleton } from '../components/skeletons'
import { PAGE_BANNER_PRIMARY_BTN_SX } from '../theme/constants'

const Catalog: React.FC = () => {
  const navigate = useNavigate()
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const { showNotification } = useNotification()
  const { smbs, selectedSMBId, setSelectedSMBId, loading: smbsLoading } = useSMBContext()
  const [content, setContent] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [tagQuery, setTagQuery] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [contentTypeFilter, setContentTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<ContentItem | null>(null)

  const loadContent = useCallback(async (isRefresh: boolean = false) => {
    if (!selectedSMBId) return
    
    if (isRefresh) {
      setIsRefreshing(true)
    } else {
      setLoading(true)
    }
    try {
      const response = await catalogApi.getContent({
        smb_id: selectedSMBId,
        type: contentTypeFilter || undefined,
        tag: tagQuery || undefined,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        limit: 50
      })
      
      // Handle both direct array and paginated/object response formats
      let contentData = []
      if (Array.isArray(response)) {
        contentData = response
      } else if (response && typeof response === 'object') {
        contentData = response.items || response.content || response.data || []
      }
      
      setContent(contentData)
    } catch (error: any) {
      if (error.response?.status === 422) {
        showNotification('Something went wrong. Please refresh.', 'error')
      } else if (error.response?.status === 404) {
        showNotification('Content not found', 'error')
      } else {
        showNotification(error.response?.data?.detail || 'Failed to load content', 'error')
      }
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [selectedSMBId, contentTypeFilter, tagQuery, searchQuery, statusFilter, showNotification])

  useEffect(() => {
    if (selectedSMBId) {
      loadContent()
    }
  }, [selectedSMBId, loadContent])

  // Debounce for search and tags
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setTagQuery(tagInput)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, tagInput])



  const handleDeleteClick = (item: ContentItem) => {
    setItemToDelete(item)
    setDeleteDialogOpen(true)
  }

  const handleStatusToggle = async (item: ContentItem, newStatus: 'active' | 'inactive') => {
    if (!selectedSMBId) return
    try {
      await catalogApi.toggleContentStatus(item.id, selectedSMBId, newStatus)
      showNotification(`Content marked as ${newStatus}`, 'success')
      // Update local state directly for snappy UX
      setContent(prev => prev.map(c => c.id === item.id ? { ...c, status: newStatus } : c))
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to update status', 'error')
    }
  }

  const handleEditClick = (item: ContentItem) => {
    setEditingItem(item)
    setAddDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!itemToDelete || !selectedSMBId) return
    try {
      if (itemToDelete.has_integration) {
        await catalogApi.deleteSMBProduct(itemToDelete.id, selectedSMBId)
      } else {
        await catalogApi.deleteContent(itemToDelete.id, selectedSMBId)
      }
      showNotification('Content deleted successfully', 'success')
      loadContent(true)
    } catch (error: any) {
      if (error.response?.status === 422) {
        showNotification('Something went wrong. Please refresh.', 'error')
      } else if (error.response?.status === 404) {
        showNotification('Content not found', 'error')
      } else {
        showNotification(error.response?.data?.detail || 'Failed to delete content', 'error')
      }
    } finally {
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }

  return (
    <Box>
      {/* Header */}
      <PageBanner
        title="Catalog"
        subtitle="Manage and organize your multi-format content"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={loading || smbsLoading || !selectedSMBId}
            sx={PAGE_BANNER_PRIMARY_BTN_SX}
          >
            Add Content
          </Button>
        }
        onRefresh={() => loadContent(true)}
        refreshing={isRefreshing}
      />

      {/* SMB Selector */}
      <Box sx={{ mb: 3 }}>
        <SMBSelector
          smbs={smbs}
          selectedSMBId={selectedSMBId}
          onChange={setSelectedSMBId}
        />
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <ContentFilters
            searchQuery={searchInput}
            onSearchChange={setSearchInput}
            contentType={contentTypeFilter}
            onContentTypeChange={setContentTypeFilter}
            tagFilter={tagInput}
            onTagFilterChange={setTagInput}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </CardContent>
      </Card>

      {/* Content Grid */}
      {smbsLoading || (loading && !isRefreshing && selectedSMBId) ? (
        <CatalogGridSkeleton count={6} />
      ) : (smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (selectedSMBId && !smbs.find((s) => s.id === selectedSMBId)?.onboarding_completed)) ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'background.paper',
            borderColor: 'divider',
          }}
        >
          {hasSubscription === false ? (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Subscription Required"
              description="An active plan is required to manage Catalog. Choose a plan to start managing your products."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Choose Plan
                </Button>
              }
              minHeight={360}
            />
          ) : (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Add a Business to Get Started"
              description="You haven't added a business yet. Connect your WhatsApp business to upload content."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Add Business
                </Button>
              }
              minHeight={360}
            />
          )}
        </Paper>
      ) : !selectedSMBId ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'background.paper',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<StorefrontIcon />}
            title="No Business Selected"
            description="Choose a business from the dropdown above to manage its catalog."
            minHeight={360}
          />
        </Paper>
      ) : content.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'background.paper',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<LibraryBooksIcon />}
            title="No catalog content found"
            description={
              searchQuery || tagQuery || contentTypeFilter || statusFilter
                ? 'No content matches your active search or filters. Try adjusting your filters.'
                : 'Add new content to the catalog.'
            }
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
                Add Content
              </Button>
            }
            minHeight={360}
          />
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {content.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <ContentCard
                item={item}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onStatusToggle={handleStatusToggle}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialogs */}
      <AddContentDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false)
          setEditingItem(null)
        }}
        onSuccess={() => {
          showNotification(editingItem ? 'Content updated successfully!' : 'Content added successfully!', 'success')
          loadContent(true)
        }}
        smbId={selectedSMBId}
        itemToEdit={editingItem}
        initialType={contentTypeFilter}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Content"
        description="Are you sure you want to delete this item? This action cannot be undone."
        confirmLabel="Delete"
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  )
}

export default Catalog
